import React, { useState, useEffect } from 'react';
import { testSupabaseConnection, testDatabaseSchema, testWebhookListener } from '../utils/connectionTest';
import { AlertTriangle, CheckCircle, RefreshCw, Database, Server, Key, Bell, Link } from 'lucide-react';
import { supabase } from '../utils/supabase';

interface ConnectionStatus {
  connected: boolean;
  error?: string;
  url?: string;
  version?: string;
  status?: number;
  method?: string;
  details?: any;
}

interface SchemaStatus {
  schemaAccess: boolean;
  error?: string;
  tableCount?: number;
  details?: any;
}

interface WebhookStatus {
  listenerAvailable: boolean;
  error?: string;
  response?: any;
  details?: any;
}

function ConnectionTest() {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
  const [schemaStatus, setSchemaStatus] = useState<SchemaStatus | null>(null);
  const [webhookStatus, setWebhookStatus] = useState<WebhookStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [directResult, setDirectResult] = useState<string | null>(null);
  
  async function runTests() {
    setLoading(true);
    try {
      // Test basic connection
      const connStatus = await testSupabaseConnection();
      setConnectionStatus(connStatus);
      
      // If connected, test schema access and webhook listener
      if (connStatus.connected) {
        const [dbStatus, whStatus] = await Promise.all([
          testDatabaseSchema(),
          testWebhookListener()
        ]);
        
        setSchemaStatus(dbStatus);
        setWebhookStatus(whStatus);
      } else {
        setSchemaStatus(null);
        setWebhookStatus(null);
      }
    } catch (error) {
      console.error('Error running tests:', error);
      setConnectionStatus({
        connected: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setLoading(false);
    }
  }
  
  async function testDirect() {
    try {
      // Try a direct raw SQL query as a last resort
      setDirectResult("Testing direct SQL query...");
      const { data, error } = await supabase.rpc('connection_test');
      
      if (error) {
        setDirectResult(`Error: ${error.message} (${error.code})`);
      } else {
        setDirectResult(`Success! ${JSON.stringify(data)}`);
      }
    } catch (error) {
      setDirectResult(`Exception: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  
  useEffect(() => {
    runTests();
  }, []);
  
  return (
    <div>
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Supabase Connection Test</h1>
          <p className="mt-2 text-sm text-gray-700">
            Verify the connection status to your Supabase instance and database schema.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={runTests}
            disabled={loading}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto disabled:bg-indigo-300"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Tests
              </>
            )}
          </button>
        </div>
      </div>
      
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Connection Status</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">Testing basic connectivity to Supabase</p>
          </div>
          {connectionStatus && (
            <div className={`px-3 py-1 rounded-full text-sm font-semibold
              ${connectionStatus.connected 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'}`}>
              {connectionStatus.connected ? 'Connected' : 'Failed'}
            </div>
          )}
        </div>
        <div className="border-t border-gray-200">
          <dl>
            {!connectionStatus ? (
              <div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <div className="flex items-center">
                    <RefreshCw className="h-5 w-5 text-gray-400 animate-spin mr-2" />
                    Testing connection...
                  </div>
                </dd>
              </div>
            ) : connectionStatus.connected ? (
              <>
                <div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 bg-green-50">
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    Connected successfully 
                    {connectionStatus.method && (
                      <span className="ml-2 text-xs text-gray-500">
                        via {connectionStatus.method}
                      </span>
                    )}
                  </dd>
                </div>
                <div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Supabase URL</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex items-center">
                    <Server className="h-5 w-5 text-gray-400 mr-2" />
                    {connectionStatus.url}
                  </dd>
                </div>
                <div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Database Version</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex items-center">
                    <Database className="h-5 w-5 text-gray-400 mr-2" />
                    {connectionStatus.version}
                  </dd>
                </div>
              </>
            ) : (
              <>
                <div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 bg-red-50">
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex items-center">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
                    Connection failed
                  </dd>
                </div>
                <div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Error Message</dt>
                  <dd className="mt-1 text-sm text-red-600 sm:mt-0 sm:col-span-2">
                    {connectionStatus.error}
                  </dd>
                </div>
              </>
            )}
          </dl>
        </div>
      </div>
      
      {connectionStatus?.connected && (
        <>
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">Database Schema Access</h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">Testing access to database tables and RLS policies</p>
              </div>
              {schemaStatus && (
                <div className={`px-3 py-1 rounded-full text-sm font-semibold
                  ${schemaStatus.schemaAccess 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'}`}>
                  {schemaStatus.schemaAccess ? 'Access Granted' : 'Access Denied'}
                </div>
              )}
            </div>
            <div className="border-t border-gray-200">
              <dl>
                {!schemaStatus ? (
                  <div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      <div className="flex items-center">
                        <RefreshCw className="h-5 w-5 text-gray-400 animate-spin mr-2" />
                        Testing schema access...
                      </div>
                    </dd>
                  </div>
                ) : schemaStatus.schemaAccess ? (
                  <>
                    <div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 bg-green-50">
                      <dt className="text-sm font-medium text-gray-500">Status</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                        Schema access successful
                      </dd>
                    </div>
                    <div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Users Count</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {schemaStatus.tableCount}
                      </dd>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 bg-red-50">
                      <dt className="text-sm font-medium text-gray-500">Status</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex items-center">
                        <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
                        Schema access failed
                      </dd>
                    </div>
                    <div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Error Message</dt>
                      <dd className="mt-1 text-sm text-red-600 sm:mt-0 sm:col-span-2">
                        {schemaStatus.error}
                      </dd>
                    </div>
                  </>
                )}
              </dl>
            </div>
          </div>
          
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">Webhook Listener</h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">Testing if the webhook listener function is available</p>
              </div>
              {webhookStatus && (
                <div className={`px-3 py-1 rounded-full text-sm font-semibold
                  ${webhookStatus.listenerAvailable 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'}`}>
                  {webhookStatus.listenerAvailable ? 'Available' : 'Unavailable'}
                </div>
              )}
            </div>
            <div className="border-t border-gray-200">
              <dl>
                {!webhookStatus ? (
                  <div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      <div className="flex items-center">
                        <RefreshCw className="h-5 w-5 text-gray-400 animate-spin mr-2" />
                        Testing webhook listener...
                      </div>
                    </dd>
                  </div>
                ) : webhookStatus.listenerAvailable ? (
                  <>
                    <div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 bg-green-50">
                      <dt className="text-sm font-medium text-gray-500">Status</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                        Webhook listener available
                      </dd>
                    </div>
                    <div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Response</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex items-center">
                        <Bell className="h-5 w-5 text-gray-400 mr-2" />
                        {typeof webhookStatus.response === 'string' 
                          ? webhookStatus.response 
                          : JSON.stringify(webhookStatus.response)}
                      </dd>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 bg-yellow-50">
                      <dt className="text-sm font-medium text-gray-500">Status</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex items-center">
                        <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
                        Webhook listener unavailable
                      </dd>
                    </div>
                    <div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Error Message</dt>
                      <dd className="mt-1 text-sm text-yellow-600 sm:mt-0 sm:col-span-2">
                        {webhookStatus.error}
                      </dd>
                    </div>
                  </>
                )}
              </dl>
            </div>
          </div>
        </>
      )}
      
      <div className="mt-6">
        <button
          onClick={() => testDirect()}
          className="mr-4 inline-flex items-center justify-center rounded-md border border-indigo-500 bg-white px-4 py-2 text-sm font-medium text-indigo-600 shadow-sm hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          <Link className="h-4 w-4 mr-2" />
          Test Direct Connection
        </button>
        
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="text-sm text-indigo-600 hover:text-indigo-800"
        >
          {showDetails ? 'Hide Technical Details' : 'Show Technical Details'}
        </button>
        
        {directResult && (
          <div className="mt-4 p-4 bg-gray-50 rounded-md">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Direct Test Result:</h4>
            <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto">{directResult}</pre>
          </div>
        )}
        
        {showDetails && (
          <div className="mt-4 bg-gray-50 rounded-md p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Configuration</h3>
            <div className="mb-4">
              <div className="flex items-center">
                <Server className="h-4 w-4 text-gray-500 mr-2" />
                <span className="text-sm text-gray-700 font-medium">Supabase URL:</span>
              </div>
              <code className="block mt-1 text-sm bg-gray-100 p-2 rounded">
                {import.meta.env.VITE_SUPABASE_URL || 'Not set'}
              </code>
            </div>
            <div className="mb-4">
              <div className="flex items-center">
                <Key className="h-4 w-4 text-gray-500 mr-2" />
                <span className="text-sm text-gray-700 font-medium">Anon Key:</span>
              </div>
              <code className="block mt-1 text-sm bg-gray-100 p-2 rounded">
                {import.meta.env.VITE_SUPABASE_ANON_KEY ? 
                  `${import.meta.env.VITE_SUPABASE_ANON_KEY.substring(0, 24)}...` : 
                  'Not set'}
              </code>
            </div>
            
            <h3 className="text-lg font-medium text-gray-900 mb-2 mt-6">Raw Response Data</h3>
            <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-64">
              {JSON.stringify({
                connectionStatus,
                schemaStatus,
                webhookStatus
              }, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConnectionTest;